/**
 * FilterPanel — the only allowed filter chrome for ERP list/report pages.
 *
 * Layout contract (do not break):
 * 1. Header (title + active count)
 * 2. Date band — full width when dateRange is set (never inside the fields grid)
 * 3. Fields grid — search + FilterPanelField children only (never date, never actions)
 * 4. Optional footer extras (switches / comparison)
 * 5. Active chips
 * 6. Actions row — Clear + Apply / extraActions only (never inline with fields)
 */
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronUp, Filter, Search, X } from 'lucide-react'
import { cn } from '../lib/utils'
import { Badge } from './ui/badge'
import { Button } from './ui/button'
import { Card, CardContent } from './ui/card'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { DateRangeFilterField } from './date-range-filter-field'
import { ClearFiltersButton } from './clear-filters-button'
import { removeUserPreference, setUserPreference } from 'services/userPreferences'

export type FilterPanelChip = {
  id: string
  label: string
  onRemove?: () => void
}

export type FilterPanelDateRange = {
  from: string
  to: string
  onChange: (next: { from: string; to: string }) => void
  showPresets?: boolean
  includeAllTimePreset?: boolean
  rememberPreset?: boolean
  includeTime?: boolean
  label?: string
}

export type FilterPanelSearch = {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  label?: string
  onSubmit?: () => void
}

type FilterPanelPreset = 'all' | '24h' | 'today' | '7d' | '30d' | '90d' | 'custom'

function toLocalDateString(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function todayLocal(): string {
  return toLocalDateString(new Date())
}

function daysAgoLocal(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() - days)
  return toLocalDateString(d)
}

/** Calendar dates covering a rolling ~24h window (yesterday → today). */
export function last24HoursDateRange(): { from: string; to: string } {
  return { from: daysAgoLocal(1), to: todayLocal() }
}

function presetToRange(preset: FilterPanelPreset): { from: string; to: string } {
  if (preset === 'all' || preset === 'custom') return { from: '', to: '' }
  if (preset === '24h') return last24HoursDateRange()
  const to = todayLocal()
  if (preset === 'today') return { from: to, to }
  if (preset === '7d') return { from: daysAgoLocal(6), to }
  if (preset === '30d') return { from: daysAgoLocal(29), to }
  return { from: daysAgoLocal(89), to }
}

function detectPreset(from: string, to: string): FilterPanelPreset {
  if (!from && !to) return 'all'
  const r24 = last24HoursDateRange()
  if (r24.from === from && r24.to === to) return '24h'
  const today = todayLocal()
  if (from === today && to === today) return 'today'
  const r7 = presetToRange('7d')
  if (r7.from === from && r7.to === to) return '7d'
  const r30 = presetToRange('30d')
  if (r30.from === from && r30.to === to) return '30d'
  const r90 = presetToRange('90d')
  if (r90.from === from && r90.to === to) return '90d'
  return 'custom'
}

const QUICK_PRESETS: FilterPanelPreset[] = ['all', '24h', 'today', '7d', '30d', '90d']
const QUICK_PRESETS_WITHOUT_ALL: FilterPanelPreset[] = ['24h', 'today', '7d', '30d', '90d']

export function FilterPanelField({
  label,
  className,
  children,
  span,
}: {
  label?: string
  className?: string
  children: React.ReactNode
  /** Column span hints for the dense grid */
  span?: 1 | 2 | 3 | 4
}) {
  return (
    <div
      className={cn(
        'flex min-w-0 flex-col gap-1',
        span === 2 && 'sm:col-span-2',
        span === 3 && 'lg:col-span-3',
        span === 4 && 'sm:col-span-2 lg:col-span-3 xl:col-span-4',
        className,
      )}
    >
      {label ? <Label className="text-[13px] font-semibold text-foreground/80">{label}</Label> : null}
      {children}
    </div>
  )
}

export type FilterPanelProps = {
  title?: string
  description?: string
  icon?: React.ReactNode
  collapsible?: boolean
  defaultOpen?: boolean
  activeCount?: number
  dateRange?: FilterPanelDateRange
  search?: FilterPanelSearch
  chips?: FilterPanelChip[]
  onApply?: () => void
  applyDisabled?: boolean
  onClear: () => void
  applyLabel?: string
  /** Prefer FilterPanelField children — never put date/actions here */
  children?: React.ReactNode
  /** Apply/Clear companions only (export buttons, etc.) — rendered in the actions row */
  extraActions?: React.ReactNode
  className?: string
  /** Switches / comparison — below the fields grid, above chips */
  footer?: React.ReactNode
  /** When true, wraps content without Card chrome (used by FilterBar inside an outer card) */
  bare?: boolean
}

export function FilterPanel({
  title,
  description,
  icon,
  collapsible = true,
  defaultOpen = false,
  activeCount = 0,
  dateRange,
  search,
  chips = [],
  onApply,
  applyDisabled = false,
  onClear,
  applyLabel,
  extraActions,
  children,
  className,
  footer,
  bare = false,
}: FilterPanelProps) {
  const { t, i18n } = useTranslation()
  const [open, setOpen] = useState(defaultOpen)
  const showBody = !collapsible || open

  const resolvedTitle = title ?? t('ui.filters.title')
  const handleClear = () => {
    if (dateRange?.rememberPreset) setUserPreference('reports.datePreset', 'all')
    onClear()
  }
  const activePreset = useMemo(
    () => (dateRange ? detectPreset(dateRange.from, dateRange.to) : 'all'),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional deps
    [dateRange?.from, dateRange?.to],
  )
  const includeAllTimePreset = dateRange?.includeAllTimePreset !== false
  const quickPresets = includeAllTimePreset ? QUICK_PRESETS : QUICK_PRESETS_WITHOUT_ALL

  const presetLabels: Record<FilterPanelPreset, string> = {
    all: t('ui.dateRange.presets.all'),
    '24h': t('ui.filters.presets.24h'),
    today: t('ui.filters.presets.today'),
    '7d': t('ui.filters.presets.7d'),
    '30d': t('ui.filters.presets.30d'),
    '90d': t('ui.filters.presets.90d'),
    custom: t('ui.filters.presets.custom'),
  }

  const header = (
    <>
      <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-primary-soft-border/60 bg-primary-soft-strong/70 text-primary shadow-sm [&_svg]:h-[18px] [&_svg]:w-[18px]">
        {icon ?? <Filter className="h-4 w-4" aria-hidden />}
      </span>
      <div className="min-w-0 flex-1 text-start">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-base font-bold tracking-tight text-foreground">{resolvedTitle}</h2>
          {activeCount > 0 ? (
            <Badge variant="secondary" className="h-6 border border-primary-soft-border/60 bg-primary-soft px-2 text-xs font-semibold text-primary-soft-foreground">
              {t('ui.filters.activeCount', { count: activeCount })}
            </Badge>
          ) : null}
        </div>
        {description && showBody ? (
          <p className="mt-1 text-sm leading-6 text-muted-foreground">{description}</p>
        ) : null}
      </div>
      {collapsible ? (
        <span className="shrink-0 text-muted-foreground" aria-hidden>
          {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </span>
      ) : null}
    </>
  )

  const chipsRow =
    chips.length > 0 ? (
      <div className="flex flex-wrap gap-1.5">
        {chips.map((chip) => (
          <button
            key={chip.id}
            type="button"
            onClick={chip.onRemove}
            disabled={!chip.onRemove}
            className={cn(
              'inline-flex max-w-full items-center gap-1 rounded-full border border-primary-soft-border/60 bg-primary-soft/60 px-3 py-1.5 text-xs font-semibold text-foreground',
              chip.onRemove && 'transition-colors hover:border-destructive/30 hover:bg-destructive/5 hover:text-destructive',
            )}
          >
            <span className="truncate">{chip.label}</span>
            {chip.onRemove ? <X className="h-3 w-3 shrink-0 opacity-70" aria-hidden /> : null}
          </button>
        ))}
      </div>
    ) : null

  const body = showBody ? (
    <div className={cn('space-y-5', !bare && 'px-5 pb-5')}>
      {/* 2. Date band — full width only */}
      {dateRange ? (
        <div className="w-full space-y-3 rounded-2xl border border-primary-soft-border/55 bg-gradient-to-l from-primary-soft/65 via-muted/25 to-card p-4 shadow-sm sm:p-5">
          <DateRangeFilterField
            label={dateRange.label}
            value={{ from: dateRange.from || null, to: dateRange.to || null }}
            onChange={({ from, to }) => dateRange.onChange({ from: from ?? '', to: to ?? '' })}
            includeAllTimePreset={includeAllTimePreset}
            rememberPreset={dateRange.rememberPreset}
            includeTime={dateRange.includeTime}
            className="w-full max-w-none"
          />
          {dateRange.showPresets !== false && !dateRange.includeTime ? (
            <div className="flex flex-wrap gap-1.5 rounded-xl border border-primary-soft-border/55 bg-card/90 p-1.5 shadow-sm">
              {quickPresets.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => {
                    if (dateRange.rememberPreset) {
                      if (preset === 'all') setUserPreference('reports.datePreset', 'all')
                      else if (preset === 'today') setUserPreference('reports.datePreset', 'today')
                      else if (preset === '24h') setUserPreference('reports.datePreset', 'last24Hours')
                      else if (preset === '7d') setUserPreference('reports.datePreset', 'last7Days')
                      else if (preset === '30d') setUserPreference('reports.datePreset', 'last30Days')
                      else if (preset === '90d') setUserPreference('reports.datePreset', 'last90Days')
                      else removeUserPreference('reports.datePreset')
                    }
                    dateRange.onChange(presetToRange(preset))
                  }}
                  className={cn(
                    'rounded-lg px-3.5 py-2 text-xs font-semibold transition-all',
                    activePreset === preset
                      ? 'bg-primary text-primary-foreground shadow-sm ring-1 ring-primary/10'
                      : 'text-muted-foreground hover:bg-primary-soft/55 hover:text-primary',
                  )}
                >
                  {presetLabels[preset]}
                </button>
              ))}
              {activePreset === 'custom' ? (
                <span className="inline-flex items-center rounded-lg border border-primary-soft-border/60 bg-primary-soft-strong px-3.5 py-2 text-xs font-semibold text-primary-soft-foreground">
                  {presetLabels.custom}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {/* 3. Fields grid — search + filters only */}
      {(search || children) ? (
        <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {search ? (
            <FilterPanelField
              label={search.label ?? t('ui.searchLabel')}
              /* Full-width first row so long AR/FR placeholders are not clipped */
              span={4}
            >
              <div className="relative min-w-0">
                <Search className="pointer-events-none absolute start-3 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-primary/70" aria-hidden />
                <Input
                  value={search.value}
                  onChange={(e) => search.onChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') search.onSubmit?.()
                  }}
                  placeholder={search.placeholder ?? t('ui.searchPlaceholder')}
                  title={search.placeholder ?? t('ui.searchPlaceholder')}
                  className="h-12 min-h-12 w-full min-w-0 rounded-xl border-border-subtle bg-card pe-3 ps-11 text-sm shadow-sm transition-all placeholder:text-muted-foreground/80 hover:border-primary-soft-border focus-visible:border-primary/40 focus-visible:ring-primary/20"
                />
              </div>
            </FilterPanelField>
          ) : null}
          {children}
        </div>
      ) : null}

      {/* 4. Extras */}
      {footer}

      {/* 5. Chips */}
      {chipsRow}

      {/* 6. Actions — Clear + Apply only */}
      <div className="flex flex-wrap items-center justify-end gap-2 border-t border-primary-soft-border/50 pt-4">
        <ClearFiltersButton count={activeCount} onClear={handleClear} />
        {extraActions}
        {onApply ? (
          <Button
            type="button"
            size="sm"
            className="rounded-xl shadow-sm"
            onClick={onApply}
            disabled={applyDisabled}
          >
            {applyLabel ?? t('ui.filters.apply')}
          </Button>
        ) : null}
      </div>
    </div>
  ) : null

  if (bare) {
    return (
      <div className={cn(className)} dir={i18n.dir()}>
        {collapsible ? (
          <button
            type="button"
            className={cn(
              'mb-4 flex w-full items-center gap-3 border-b border-border-subtle pb-3 text-start transition-colors hover:bg-primary-soft/20',
              !showBody && 'mb-0 border-b-0 pb-0',
            )}
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
          >
            {header}
          </button>
        ) : title || description ? (
          <div className="mb-4 flex items-center gap-3 border-b border-border-subtle pb-3">{header}</div>
        ) : null}
        {!showBody && chipsRow ? <div className={cn('mt-3', !bare && 'px-5')}>{chipsRow}</div> : null}
        {body}
      </div>
    )
  }

  return (
    <Card className={cn('admin-surface-card mb-5 overflow-hidden ring-1 ring-primary/5', className)} dir={i18n.dir()}>
      <CardContent className="p-0">
        {collapsible ? (
          <button
            type="button"
            className={cn(
              'group flex w-full items-center gap-3 px-5 py-4 text-start transition-colors hover:bg-primary-soft/25 sm:px-6',
              showBody && 'border-b border-primary-soft-border/50 bg-gradient-to-l from-primary-soft-strong/55 via-card to-card',
            )}
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
          >
            {header}
          </button>
        ) : (
          <div className="mb-0 flex items-center gap-3 border-b border-primary-soft-border/50 bg-gradient-to-l from-primary-soft-strong/55 via-card to-card px-5 py-4 sm:px-6">{header}</div>
        )}

        {!showBody && chipsRow ? <div className="px-5 pb-5 sm:px-6">{chipsRow}</div> : null}
        {body}
      </CardContent>
    </Card>
  )
}
